import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services';
import { gtagService } from '../services/gtagService';
import type { AuthUser } from '../services';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: AuthUser; token?: string } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: Partial<AuthUser> }
  | { type: 'SET_ERROR'; payload: string | null };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to prevent race conditions
  error: null,
  token: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN_SUCCESS':
      // Set user ID for analytics tracking
      if (action.payload.user.id) {
        gtagService.setUserId(action.payload.user.id);
        gtagService.setUserProperties({
          user_id: action.payload.user.id,
          user_type: 'customer'
        });
      }
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token || state.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (phone: string, otp: string, name?: string, email?: string) => Promise<{ success: boolean; error?: string }>;
  sendOTP: (phone: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateUser: (userData: Partial<AuthUser>) => void;
  register: (name: string, phone: string, email?: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from localStorage on mount
  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    const initAuth = async () => {
      // Check if user is authenticated
      if (authService.isAuthenticated()) {
        const user = authService.getCurrentUser();
        const token = authService.getToken();
        if (user) {
          // Try to refresh user data from API
          try {
            const response = await authService.getCurrentUserFromAPI();
            if (response.success && response.data) {
              dispatch({ type: 'LOGIN_SUCCESS', payload: { user: response.data, token: token || undefined } });
            } else {
              dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: token || undefined } });
            }
          } catch {
            // If API fails, use cached user
            dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: token || undefined } });
          }
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initAuth();
  }, []);

  // Listen for token expiration events
  useEffect(() => {
    const handleTokenExpired = () => {
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, []);

  const sendOTP = async (phone: string): Promise<{ success: boolean; error?: string }> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const response = await authService.generateOTP(phone);
      
      if (response.success) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: true };
      } else {
        const errorMessage = response.error || 'Failed to send OTP';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send OTP';
      console.error('Error sending OTP:', error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const login = async (phone: string, otp: string, name?: string, _email?: string): Promise<{ success: boolean; error?: string; requiresRegistration?: boolean }> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const response = await authService.verifyOTP(phone, otp, name);
      
      if (response.success && response.data) {
        // Check if registration is required
        if (response.data.requiresRegistration) {
          dispatch({ type: 'SET_LOADING', payload: false });
          return { success: true, requiresRegistration: true };
        }
        
        // Normal login flow
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user: response.data.user, token: response.data.token } });
        return { success: true };
      } else {
        const errorMessage = response.error || 'Invalid OTP or login failed';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      console.error('Error during login:', error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const register = async (name: string, phone: string, email?: string, _otp?: string): Promise<{ success: boolean; error?: string }> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const response = await authService.register({ name, phone, email });
      
      if (response.success) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: true };
      } else {
        const errorMessage = response.error || 'Registration failed';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      console.error('Error during registration:', error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const updateUser = async (userData: Partial<AuthUser>): Promise<void> => {
    if (state.user) {
      try {
        const response = await authService.updateProfile(userData);
        if (response.success && response.data) {
          dispatch({ type: 'UPDATE_USER', payload: response.data });
        }
      } catch (error) {
        console.error('Error updating user:', error);
        // Still update locally even if API fails
        dispatch({ type: 'UPDATE_USER', payload: userData });
      }
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const value: AuthContextType = {
    ...state,
    login,
    sendOTP,
    logout,
    updateUser,
    register,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};